import { useQuery } from '@tanstack/react-query';
import { cpFetch } from './cpClient';
import { getPollMs, isPageVisible } from './perfMode';
import { useAuthStore } from '../store/authStore';

export type ServiceHealthComponent = {
  status?: string;
  ok?: boolean;
  latency_ms?: number;
  observed_at?: string;
  reason?: string | null;
  details?: unknown;
  mode?: string | null;
};

export type ServicesStatusPayload = {
  ok?: boolean;
  signal?: string;
  updated_at?: string;
  platform_build?: string | null;
  platform_stamp_status?: string | null;
  cache?: {
    ttl_ms?: number;
    age_ms?: number;
    fresh?: boolean;
  };
  summary?: {
    total?: number;
    ok?: number;
    degraded?: number;
    down?: number;
  };
  components?: Record<string, ServiceHealthComponent>;
  services?: Array<{
    name: string;
    status?: string;
    ok?: boolean;
    observedAt?: string;
    mode?: string | null;
    reason?: string | null;
  }>;
};

export const SERVICE_STATUS_QUERY_KEY = ['cp-services-status'] as const;

/** Shared poll: one snapshot, no per-route remount refetch that can disagree. */
export const SERVICE_STATUS_STALE_MS = 30_000;

export const SERVICE_STATUS_QUERY_POLICY = {
  staleTime: SERVICE_STATUS_STALE_MS,
  gcTime: Infinity,
  refetchOnMount: false as const,
  refetchOnWindowFocus: false as const,
  refetchOnReconnect: true as const,
};

const LATCH_STORAGE_KEY = 'dc.serviceHealth.latch';

export function statusInventorySize(payload?: ServicesStatusPayload | null): number {
  if (payload?.components && Object.keys(payload.components).length > 0) {
    return Object.keys(payload.components).length;
  }
  if (Array.isArray(payload?.services) && payload.services.length > 0) {
    return payload.services.length;
  }
  return 0;
}

/**
 * Chrome snapshot picker. A slim/signal-only payload must never replace a
 * probe list (the System Health inventory). Route-scoped fetches that lose
 * auth or return F15 `{ ok, signal }` were minting a second n/n.
 */
export function pickChromeStatusPayload(
  current: ServicesStatusPayload | null | undefined,
  incoming: ServicesStatusPayload | null | undefined,
): ServicesStatusPayload | null | undefined {
  if (!incoming) return current ?? incoming;
  const incomingSize = statusInventorySize(incoming);
  const currentSize = statusInventorySize(current);
  if (currentSize > 0 && incomingSize === 0) return current;
  return incoming;
}

export function readLatchedStatusPayload(): ServicesStatusPayload | null {
  try {
    const raw = sessionStorage.getItem(LATCH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed as ServicesStatusPayload : null;
  } catch {
    return null;
  }
}

export function writeLatchedStatusPayload(payload: ServicesStatusPayload | null | undefined): void {
  if (!payload || typeof payload !== 'object') return;
  const next = pickChromeStatusPayload(readLatchedStatusPayload(), payload);
  if (!next) return;
  try {
    sessionStorage.setItem(LATCH_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / private-mode
  }
}

/**
 * Owner-only React Query for /cp/services/status.
 * Pages must read ServiceHealthProvider — a second observer still gets its
 * own refetchInterval and can overwrite chrome with a flaky 25/27.
 */
export function useServicesStatus(enabled = true, options?: { owner?: boolean }) {
  const { token } = useAuthStore();
  const owner = options?.owner === true;

  return useQuery({
    queryKey: SERVICE_STATUS_QUERY_KEY,
    enabled: enabled && Boolean(token),
    queryFn: async () => {
      if (!token) throw new Error('No token');
      return await cpFetch('/services/status') as ServicesStatusPayload;
    },
    ...SERVICE_STATUS_QUERY_POLICY,
    placeholderData: (previous) => previous ?? readLatchedStatusPayload() ?? undefined,
    refetchInterval: owner
      ? () => (isPageVisible() ? getPollMs(SERVICE_STATUS_STALE_MS) : false)
      : false,
  });
}
